import { sort } from '@engineering11/utility'
import { ITraitDots } from '../core/culture-index.model'

export interface ICultureIndexJobPost {
  id: string
  cJobs: ICJob[]
}

export interface ICJob {
  id: number // positionAnswerId
  isIdeal: boolean
  // isTypified: boolean
  patternName: string // Influencer, Operator etc...
  completedByName: string
  createdDate: string
  traitDots: ITraitDots
  isApproved: boolean
}

export enum CJobCategory {
  Research = 'Research',
  Visionary = 'Visionary',
  Organizational = 'Organizational',
  Social = 'Social',
  Other = 'Other',
}

export enum CJobPattern {
  // Research
  Scholar = 'Scholar',
  Specialist = 'Specialist',
  TechnicalExpert = 'Technical Expert',
  TechnicalExpertHighB = 'Technical Expert (High B)',
  Craftsman = 'Craftsman',
  CraftsmanLeadingD = 'Craftsman (Leading D)',
  CraftsmanLowerDABStack = 'Craftsman (Lower D with AB Stack)',
  CraftsmanLowerD = 'Craftsman (Lower D)',

  // Visionary
  Enterpriser = 'Enterpriser',
  Trailblazer = 'Trailblazer',
  Daredevil = 'Daredevil',
  Architect = 'Architect',
  ArchitectHighB = 'Architect (High B)',
  Philosopher = 'Philosopher',

  // Organizational
  Operator = 'Operator',
  Facilitator = 'Facilitator',
  Administrator = 'Administrator',
  AdministratorABStack = 'Administrator (AB Stack)',
  Coordinator = 'Coordinator',
  Traditionalist = 'Traditionalist',

  // Social
  Debater = 'Debater',
  Socializer = 'Socializer',
  Rainmaker = 'Rainmaker',
  Persuader = 'Persuader',
  Influencer = 'Influencer',

  // Other
  Avoidant = 'Avoidant',
  Chameleon = 'Chameleon',
  NoSurvey = 'No Survey',
}

export enum CultureIndexColor {
  BLUE = '#0040B8',
  RED = '#B8002B',
  GREEN = '#009440',
  YELLOW = '#FFCC00',
  GRAY = '#6F7376',
}

export enum CultureIndexTextColor {
  WHITE = '#FFFFFF',
  BLACK = '#000000',
}

export type CJobPatternCategory = {
  [C in CJobCategory]: CJobPattern[]
}

export interface CJobCategoryToPattern {
  category: CJobCategory
  patterns: CJobPattern[]
  color: CultureIndexColor
  secondaryColor: CultureIndexTextColor
  textColor: CultureIndexTextColor
  secondaryTextColor: CultureIndexColor
}

export interface CJobPatternToCategory {
  pattern: CJobPattern
  category: CJobCategory
  color: CultureIndexColor
  secondaryColor: CultureIndexTextColor
  textColor: CultureIndexTextColor
  secondaryTextColor: CultureIndexColor
}

/**
 * @deprecated use @link {C_JOB_CATEGORY_PATTERN_MAP} instead for optimized lookup
 */
export const C_JOB_CATEGORY_PATTERNS: CJobCategoryToPattern[] = [
  {
    category: CJobCategory.Research,
    patterns: [
      CJobPattern.Scholar,
      CJobPattern.Specialist,
      CJobPattern.TechnicalExpert,
      CJobPattern.TechnicalExpertHighB,
      CJobPattern.Craftsman,
      CJobPattern.CraftsmanLeadingD,
      CJobPattern.CraftsmanLowerDABStack,
      CJobPattern.CraftsmanLowerD,
    ],
    color: CultureIndexColor.BLUE,
    secondaryColor: CultureIndexTextColor.WHITE,
    textColor: CultureIndexTextColor.WHITE,
    secondaryTextColor: CultureIndexColor.BLUE,
  },
  {
    category: CJobCategory.Visionary,
    patterns: [
      CJobPattern.Enterpriser,
      CJobPattern.Trailblazer,
      CJobPattern.Daredevil,
      CJobPattern.Architect,
      CJobPattern.ArchitectHighB,
      CJobPattern.Philosopher,
    ],
    color: CultureIndexColor.RED,
    secondaryColor: CultureIndexTextColor.WHITE,
    textColor: CultureIndexTextColor.WHITE,
    secondaryTextColor: CultureIndexColor.RED,
  },
  {
    category: CJobCategory.Organizational,
    patterns: [
      CJobPattern.Operator,
      CJobPattern.Facilitator,
      CJobPattern.Administrator,
      CJobPattern.AdministratorABStack,
      CJobPattern.Coordinator,
      CJobPattern.Traditionalist,
    ],
    color: CultureIndexColor.GREEN,
    secondaryColor: CultureIndexTextColor.WHITE,
    textColor: CultureIndexTextColor.WHITE,
    secondaryTextColor: CultureIndexColor.GREEN,
  },
  {
    category: CJobCategory.Social,
    patterns: [CJobPattern.Debater, CJobPattern.Socializer, CJobPattern.Rainmaker, CJobPattern.Persuader, CJobPattern.Influencer],
    color: CultureIndexColor.YELLOW,
    secondaryColor: CultureIndexTextColor.WHITE,
    textColor: CultureIndexTextColor.BLACK,
    secondaryTextColor: CultureIndexColor.GRAY,
  },
  {
    category: CJobCategory.Other,
    patterns: [CJobPattern.Avoidant, CJobPattern.Chameleon, CJobPattern.NoSurvey],
    color: CultureIndexColor.GRAY,
    secondaryColor: CultureIndexTextColor.WHITE,
    textColor: CultureIndexTextColor.WHITE,
    secondaryTextColor: CultureIndexColor.GRAY,
  },
]

export const C_JOB_CATEGORY_PATTERN_MAP: Record<CJobCategory, CJobCategoryToPattern> = C_JOB_CATEGORY_PATTERNS.reduce((acc, item) => {
  acc[item.category] = {
    ...item,
    patterns: sort(item.patterns),
  }
  return acc
}, {} as Record<CJobCategory, CJobCategoryToPattern>)

/**
 * @deprecated use @link {C_JOB_PATTERN_CATEGORY_MAP} instead for optimized lookup
 */
export const C_JOB_PATTERN_CATEGORIES: CJobPatternToCategory[] = C_JOB_CATEGORY_PATTERNS.reduce((acc, { patterns, ...rest }) => {
  patterns.forEach(pattern => {
    acc.push({ pattern, ...rest })
  })
  return acc
}, [] as CJobPatternToCategory[])

export const C_JOB_PATTERN_CATEGORY_MAP: Record<CJobPattern, CJobPatternToCategory> = C_JOB_PATTERN_CATEGORIES.reduce((acc, item) => {
  acc[item.pattern] = item
  return acc
}, {} as Record<CJobPattern, CJobPatternToCategory>)

export enum QuestionnaireInviteStatus {
  ACCEPTED = 'accepted',
  PENDING = 'pending',
}
export interface IQuestionnaireInvite {
  appUserId: string
  status: QuestionnaireInviteStatus
  date: Date
}

// Single question for a c-job questionnaire
export interface ICJobQuestion {
  questionNumber: number
  question: string
  languageId: number
  language: {
    languageId: number
    language: string
  }
  // options - if empty, this is a scored question (1-10), otherwise it is a single select of the available options
  questionAnswers: { sortOrder: number; displayText: string }[]
}

// Expected payload structure to backend when submitting a c-job questionnaire
export interface ICJobSurveyResponsesDTO {
  answer: { questionNumber: number; answer: number }[]
}

// integration_culture_index_jobs collection document interface
export interface IIntegrationCultureIndexJob {
  id: string // job post content id
  companyCode: string
  customerKey: string
  positionId: number
}

// Payload structure for setting a pattern as the "ideal" pattern
export interface ISetIdealDTO {
  positionAnswerId: number
}

export interface ICJobInviteDto {
  emails: string[]
}

export interface ISurveyMatchResult {
  surveyTakerTraitDots: string
  behaviorDots: string
  idealPatternTraitDots: string
  matchReportUrl?: string
  surveyReportUrl: string
  matcherPercentage: number
  patternName: string
  completedDate: Date
}

export interface IApprovedCJobPattern {
  id: number
  grouping: string
  patternName: string
}

export interface TypifiedPatternRequest {
  note: string
  typifiedPatternIds: number[]
  suggestedIdeal?: number
}

export interface IPendingTypifiedRequestsData {
  id: string
  requestedBy: string
  requestedAt: Date
  requestedPatterns: string[]
  notes?: string
}
